import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { RestService } from '../http/rest';
import { UserDataService } from '../services/user-data.service';

export interface User {
  userName?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  subscriptionValid?: boolean;
  [key: string]: unknown;
}

export interface LoginResponse {
  access_token: string;
  userName?: string;
  [key: string]: unknown;
}

export interface RegisterModel {
  userName: string;
  password: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  aff?: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class Auth {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly rest = inject(RestService);
  private readonly userDataService = inject(UserDataService);

  private readonly _user = signal<User | null>(null);
  private readonly _token = signal<string | null>(this.readTokenFromStorage());

  readonly user = this._user.asReadonly();
  readonly token = this._token.asReadonly();
  readonly isLoggedIn = computed(() => this._token() !== null);

  getIsLoggedIn(): boolean {
    return this.isLoggedIn();
  }

  setAuthState(isLoggedIn: boolean): void {
    if (isLoggedIn) {
      this._token.set(this.readTokenFromStorage());
    } else {
      this.clearSession();
    }
  }

  loadUser(): Observable<User | null> {
    if (!this.isLoggedIn()) {
      return of(null);
    }
    return this.rest.get<User>('user').pipe(
      tap((user) => {
        this._user.set(user ?? null);
        if (user?.userName) {
          this.userDataService.setUserName(user.userName);
        }
      }),
      catchError(() => {
        this._user.set(null);
        return of(null);
      }),
    );
  }

  /**
   * One-shot call after issuing a fresh access_token (login / register / password change).
   * Registers the new token's hash in AuthToken table on the backend.
   * MUST NOT be called again for the same token (violates PK_AUTHTOKEN).
   */
  registerToken(): Observable<unknown> {
    return this.rest.get<unknown>('boot').pipe(catchError(() => of(null)));
  }

  setSubscriptionValid(valid: boolean): void {
    this._user.update((u) => (u ? { ...u, subscriptionValid: valid } : u));
  }

  login(model: { userName: string; password: string }): Observable<LoginResponse> {
    const body = new HttpParams()
      .set('grant_type', 'password')
      .set('username', model.userName)
      .set('password', model.password);

    return this.http
      .post<LoginResponse>(`${environment.apiUrl}token`, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      })
      .pipe(
        tap((data) => this.applyLoginResponse(data, model.userName)),
      );
  }

  register(model: RegisterModel): Observable<unknown> {
    return this.rest.post<unknown>('register', model);
  }

  checkUserName(userName: string): Observable<boolean> {
    return this.rest.get<boolean>('register', { id: userName.trim() });
  }

  applyLoginResponse(data: LoginResponse | null | undefined, fallbackUserName?: string): void {
    if (!data?.access_token || !this.isBrowser()) return;
    localStorage.setItem('auth_token', data.access_token);
    this._token.set(data.access_token);
    const userName = data.userName ?? fallbackUserName;
    if (userName) {
      this.userDataService.setUserName(userName);
    }
    this.registerToken().subscribe({
      complete: () => this.loadUser().subscribe(),
    });
  }

  logout(): void {
    this.clearSession();
    this.router.navigate(['/login']);
  }

  private clearSession(): void {
    if (this.isBrowser()) {
      localStorage.removeItem('auth_token');
    }
    this._token.set(null);
    this._user.set(null);
    this.userDataService.clearUser();
  }

  private readTokenFromStorage(): string | null {
    if (!this.isBrowser()) return null;
    return localStorage.getItem('auth_token');
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId) && typeof window !== 'undefined' && !!window.localStorage;
  }
}
